/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useRoute, useNavigation, useFocusEffect } from '@react-navigation/native';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { SaleDetailModal } from '../../sales/components/SaleDetailModal';
import { getMergedActiveCashSessionId } from '../../cash/infra-api';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import { TicketButton } from '../../shared/ticket/TicketButton'
import { TicketSheet } from '../../shared/ticket/TicketSheet'
import { ticketControls } from '../../shared/ticket/ticketControls.styles'
import { ticketText } from '../../shared/ticket/ticketText'
import { OperationalScrollScreen } from '../../shared/ticket/OperationalScrollScreen';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { showSuccess, showError } from '../../shared/ui/appToast'
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import {
  getCommissionSettlementDetail,
  approveCommissionSettlement,
  payCommissionSettlement,
  cancelCommissionSettlement,
  voidCommissionSettlement,
} from '../infra/commissionsRepo';
import type { SettlementDetail, PaymentMethod, SettlementStatus } from '../domain/commissionTypes';
import {
  SETTLEMENT_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  commissionEarningTitle,
} from '../domain/commissionTypes';
import { commissionErrorMessage, breadcrumbCommission } from '../application-api';
import { SettlementReasonSheet } from '../components/SettlementReasonSheet';
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { formatMoney } from '../../shared/format/public-api';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch {
    return iso;
  }
}

const STATUS_TONE: Record<SettlementStatus, 'ok' | 'warn' | 'bad' | 'muted'> = {
  draft: 'muted',
  approved: 'warn',
  paid: 'ok',
  voided: 'bad',
};

type SettlementDetailNav = { goBack: () => void };

export function SettlementDetailScreen() {
  const { layoutWide } = useContentLayout('dataWide');
  const route = useRoute<{ key: string; name: string; params?: { settlementId?: string } }>();
  const navigation = useNavigation<SettlementDetailNav>();
  const settlementId = route?.params?.settlementId as string;
  const { canEdit, canVoid, actions } = useBranchPermission('Commissions');
  const { entitlements } = usePlanEntitlements();
  const isOwner = entitlements?.is_owner === true;

  const canApprove = isOwner || actions.approve === true || canEdit;
  const canPay = isOwner || actions.pay === true || canEdit;
  const canVoidSettlement = isOwner || actions.void === true || canVoid;
  const canSettle = isOwner || actions.settle === true || canEdit;
  const capabilityGate = useCapabilityGate('commissions', {
    title: 'Comisiones no disponibles',
    subtitle: 'Este rubro no tiene comisiones.',
  });

  const { confirm, ConfirmModal } = useOperationalConfirm();

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<SettlementDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [paymentRef, setPaymentRef] = useState('');
  const [payNote, setPayNote] = useState('');
  const [reasonModal, setReasonModal] = useState<'void' | 'cancel' | null>(null);
  const branchId = useSessionStore((s) => s.currentBranchId);
  const branches = useSessionStore((s) => s.branches);
  const [detailSaleId, setDetailSaleId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!settlementId) {
      setError('ID de liquidación no proporcionado.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const d = await getCommissionSettlementDetail(settlementId);
      setDetail(d);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar la liquidación.');
    } finally {
      setLoading(false);
    }
  }, [settlementId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const handleApprove = useCallback(async () => {
    if (busy) return;
    const ok = await confirm({
      title: 'Aprobar liquidación',
      message: '¿Confirmás la aprobación de esta liquidación?',
      confirmLabel: 'Aprobar',
    });
    if (!ok) return;
    setBusy(true);
    breadcrumbCommission('settlement_approve', 'approve_commission_settlement invoked', { settlementId });
    try {
      const result = await approveCommissionSettlement(settlementId);
      if (result.ok) {
        showSuccess('Aprobada', 'Liquidación aprobada');
        void load();
      } else {
        showError('Error', commissionErrorMessage(result.error, 'No se pudo aprobar.'));
      }
    } catch (e: unknown) {
      showError('Error', commissionErrorMessage(e instanceof Error ? e.message : null, 'Error al aprobar.'));
    } finally {
      setBusy(false);
    }
  }, [busy, confirm, settlementId, load]);

  const handlePay = useCallback(async (method: PaymentMethod) => {
    if (busy) return;
    const ok = await confirm({
      title: 'Pagar liquidación',
      message: `¿Confirmás el pago por ${PAYMENT_METHOD_LABELS[method]}?`,
      confirmLabel: 'Pagar',
    });
    if (!ok) return;
    setBusy(true);
    breadcrumbCommission('settlement_pay', 'pay_commission_settlement invoked', {
      settlementId,
      paymentMethod: method,
    });
    try {
      let cashSessionId: string | null = null;
      if (method === 'cash' && branchId) {
        cashSessionId = await getMergedActiveCashSessionId(branchId);
        if (!cashSessionId) {
          showError(
            'Sin caja abierta',
            'Para pagar en efectivo, abrí una caja en la sucursal actual primero.',
          );
          return;
        }
      }
      const result = await payCommissionSettlement({
        settlementId,
        paymentMethod: method,
        paymentRef: paymentRef.trim() || null,
        note: payNote.trim() || null,
        cashSessionId,
      });
      if (result.ok) {
        showSuccess('Pagada', 'Liquidación pagada');
        setPaymentRef('');
        setPayNote('');
        void load();
      } else {
        showError('Error', commissionErrorMessage(result.error, 'No se pudo pagar.'));
      }
    } catch (e: unknown) {
      showError('Error', commissionErrorMessage(e instanceof Error ? e.message : null, 'Error al pagar.'));
    } finally {
      setBusy(false);
    }
  }, [busy, confirm, settlementId, load, paymentRef, payNote, branchId]);

  const handleVoid = useCallback(async () => {
    if (busy) return;
    setReasonModal('void');
  }, [busy]);

  const handleCancel = useCallback(async () => {
    if (busy) return;
    setReasonModal('cancel');
  }, [busy]);

  const handleReasonConfirm = useCallback(async (reason: string) => {
    if (!reasonModal || busy) return;
    setBusy(true);
    breadcrumbCommission(
      reasonModal === 'void' ? 'settlement_void' : 'settlement_cancel',
      reasonModal === 'void' ? 'void_commission_settlement invoked' : 'cancel_commission_settlement invoked',
      { settlementId },
    );
    try {
      const result = reasonModal === 'void'
        ? await voidCommissionSettlement(settlementId, reason)
        : await cancelCommissionSettlement(settlementId, reason);
      if (result.ok) {
        showSuccess(
          reasonModal === 'void' ? 'Anulada' : 'Descartada',
          reasonModal === 'void' ? '' : 'Liquidación descartada',
        );
        setReasonModal(null);
        void load();
      } else {
        showError(
          'Error',
          commissionErrorMessage(
            result.error,
            reasonModal === 'void' ? 'No se pudo anular.' : 'No se pudo descartar.',
          ),
        );
      }
    } catch (e: unknown) {
      showError(
        'Error',
        commissionErrorMessage(
          e instanceof Error ? e.message : null,
          reasonModal === 'void' ? 'Error al anular.' : 'Error al descartar.',
        ),
      );
    } finally {
      setBusy(false);
    }
  }, [busy, reasonModal, settlementId, load]);

  if (!capabilityGate.allowed) {
    return <OperationalScrollScreen header={null} body={capabilityGate.blocked} />;
  }

  if (loading) {
    return (
      <OperationalScrollScreen
        header={null}
        body={
          <ModuleEmptyState
            variant="nest"
            loading
            loadingText="Cargando liquidación…"
            kicker="COMISIONES"
          />
        }
      />
    );
  }

  if (error || !detail || !detail.ok) {
    const detailError = detail && !detail.ok ? (detail as any).error ?? null : null;
    return (
      <OperationalScrollScreen
        header={null}
        body={
          <ModuleEmptyState
            variant="nest"
            kicker="COMISIONES"
            title="No se pudo cargar la liquidación"
            subtitle={error ?? detailError ?? 'Revisá la conexión e intentá de nuevo.'}
            primaryAction={{ label: 'Reintentar', onPress: () => void load() }}
          />
        }
      />
    );
  }

  const s = detail.settlement;
  const earnings = detail.earnings ?? [];
  const adjustments = detail.adjustments ?? [];
  const statusLabel = SETTLEMENT_STATUS_LABELS[s.status];
  const statusTone = STATUS_TONE[s.status];

  const periodMeta = `Período: ${formatDate(s.periodFrom)} → ${formatDate(s.periodTo)}`;
  const branchLabel = s.branchId ? branches.find((b) => b.id === s.branchId)?.name : null;

  const metaLines: string[] = [];
  if (branchLabel) metaLines.push(`Sucursal: ${branchLabel}`);
  if (s.note) metaLines.push(`Nota: ${s.note}`);
  if (s.approvedAt) metaLines.push(`Aprobada: ${formatDate(s.approvedAt)}`);
  if (s.approvedBy) metaLines.push(`Aprobada por: ${s.approvedBy}`);
  if (s.paidAt) metaLines.push(`Pagada: ${formatDate(s.paidAt)}`);
  if (s.paidBy) metaLines.push(`Pagada por: ${s.paidBy}`);
  if (s.paymentMethod) {
    metaLines.push(`Método: ${PAYMENT_METHOD_LABELS[s.paymentMethod as PaymentMethod]}`);
  }
  if (s.paymentRef) metaLines.push(`Ref. de pago: ${s.paymentRef}`);
  if (s.voidedAt) metaLines.push(`Anulada: ${formatDate(s.voidedAt)}`);
  if (s.voidedBy) metaLines.push(`Anulada por: ${s.voidedBy}`);

  const kpiItems: TicketKpiItem[] = [
    { label: 'Bruto', value: formatMoney(s.grossEarnings) },
    {
      label: 'Ajustes',
      value: formatMoney(s.adjustmentsTotal),
      tone: s.adjustmentsTotal < 0 ? 'bad' : 'ok',
    },
    { label: 'Neto', value: formatMoney(s.netPayable), tone: 'ok' },
  ];

  const showActions = canApprove || canPay || canVoidSettlement || canSettle;

  const header = (
    <TicketModuleHeader
      kicker="COMISIONES"
      title={s.employeeName}
      meta={periodMeta}
      trailing={
        <View style={styles.statusBadge}>
          <Text
            style={[
              styles.statusBadgeText,
              statusTone === 'ok' && ticketText.statusOk,
              statusTone === 'warn' && ticketText.statusWarn,
              statusTone === 'bad' && ticketText.statusBad,
              statusTone === 'muted' && ticketText.statusMuted,
            ]}
          >
            {statusLabel}
          </Text>
        </View>
      }
    >
      <View style={styles.metaList}>
        {metaLines.map((line, i) => (
          <Text key={i} style={styles.metaLine}>
            {line}
          </Text>
        ))}
      </View>
    </TicketModuleHeader>
  );

  const body = (
    <>
      {/* Totales / KPIs */}
      <PaperSheet padded>
        <TicketKpiStrip items={kpiItems} variant="soft" layout={layoutWide ? 'desk' : 'mobile'} />
      </PaperSheet>

      {/* Actions */}
      {showActions ? (
        <PaperSheet padded>
          <Text style={styles.sectionKicker}>Acciones</Text>
          {canPay && s.status === 'approved' ? (
            <View style={styles.payInputs}>
              <View style={ticketControls.field}>
                <Text style={ticketControls.fieldLabel}>Ref. de pago (opcional)</Text>
                <TextInput
                  style={ticketControls.input}
                  value={paymentRef}
                  onChangeText={setPaymentRef}
                  placeholder="Ej. transferencia #12345"
                  placeholderTextColor={ticketColors.ink3}
                  editable={!busy}
                />
              </View>
              <View style={ticketControls.field}>
                <Text style={ticketControls.fieldLabel}>Nota (opcional)</Text>
                <TextInput
                  style={ticketControls.input}
                  value={payNote}
                  onChangeText={setPayNote}
                  placeholder="Notas internas sobre el pago"
                  placeholderTextColor={ticketColors.ink3}
                  editable={!busy}
                />
              </View>
            </View>
          ) : null}
          <View style={styles.actionsRow}>
            {canApprove && s.status === 'draft' ? (
              <TicketButton
                label="Aprobar"
                variant="gold"
                onPress={handleApprove}
                disabled={busy}
                accessibilityLabel="Aprobar liquidación"
              />
            ) : null}
            {canPay && s.status === 'approved'
              ? (['cash', 'expense', 'bank_transfer', 'other'] as PaymentMethod[]).map((m) => (
                  <TicketButton
                    key={m}
                    label={`Pagar (${PAYMENT_METHOD_LABELS[m]})`}
                    variant="gold"
                    onPress={() => handlePay(m)}
                    disabled={busy}
                    accessibilityLabel={`Pagar con ${PAYMENT_METHOD_LABELS[m]}`}
                  />
                ))
              : null}
            {canSettle && (s.status === 'draft' || s.status === 'approved') ? (
              <TicketButton
                label="Descartar"
                variant="danger"
                onPress={() => void handleCancel()}
                disabled={busy}
                accessibilityLabel="Descartar liquidación"
              />
            ) : null}
            {canVoidSettlement && s.status === 'paid' ? (
              <TicketButton
                label="Anular"
                variant="danger"
                onPress={() => void handleVoid()}
                disabled={busy}
                accessibilityLabel="Anular liquidación"
              />
            ) : null}
          </View>
        </PaperSheet>
      ) : null}

      {/* Earnings */}
      <PaperSheet padded>
        <Text style={styles.sectionKicker}>Conceptos{earnings.length > 0 ? ` (${earnings.length})` : ''}</Text>
        {earnings.length === 0 ? (
          <ModuleEmptyState
            variant="nest"
            kicker="Comisiones"
            title="Sin comisiones"
            subtitle="No hay comisiones en esta liquidación."
          />
        ) : (
          earnings.map((e, i) => {
            const isLast = i === earnings.length - 1;
            const hasSaleLink = e.source_type === 'sale_item' && e.source_ref;
            return (
              <TicketListRow
                key={e.id ?? i}
                title={commissionEarningTitle(e.description)}
                meta={`Base: ${formatMoney(Number(e.commissionable_amount))} · ${formatDate(e.earned_at)}`}
                value={formatMoney(Number(e.commission_amount))}
                group={isLast ? 'groupedLast' : 'grouped'}
                footer={hasSaleLink ? (
                  <TouchableOpacity
                    onPress={() => setDetailSaleId(e.source_ref!)}
                    style={styles.saleLink}
                    accessibilityRole="button"
                    accessibilityLabel="Ver venta"
                  >
                    <Text style={styles.saleLinkText}>Ver venta →</Text>
                  </TouchableOpacity>
                ) : undefined}
              />
            );
          })
        )}
      </PaperSheet>

      {/* Adjustments */}
      <PaperSheet padded>
        <Text style={styles.sectionKicker}>Ajustes{adjustments.length > 0 ? ` (${adjustments.length})` : ''}</Text>
        {adjustments.length === 0 ? (
          <ModuleEmptyState
            variant="nest"
            kicker="Ajustes"
            title="Sin ajustes"
            subtitle="No hay ajustes en esta liquidación."
          />
        ) : (
          adjustments.map((a, i) => {
            const isLast = i === adjustments.length - 1;
            const amount = Number(a.amount);
            const metaParts: string[] = [];
            if (a.description) metaParts.push(a.description);
            metaParts.push(formatDate(a.effective_date));
            return (
              <TicketListRow
                key={a.id ?? i}
                title={a.reason}
                meta={metaParts.join(' · ')}
                value={`${amount >= 0 ? '+' : ''}${formatMoney(amount)}`}
                statusLabel={amount >= 0 ? 'Crédito' : 'Débito'}
                statusTone={amount >= 0 ? 'ok' : 'bad'}
                group={isLast ? 'groupedLast' : 'grouped'}
              />
            );
          })
        )}
      </PaperSheet>
    </>
  );

  return (
    <OperationalScrollScreen
      header={header}
      body={body}
      contentContainerStyle={styles.content}
      modals={
        <>
          <SaleDetailModal
            visible={detailSaleId != null}
            saleId={detailSaleId}
            branchId={branchId ?? null}
            onClose={() => setDetailSaleId(null)}
          />
          <SettlementReasonSheet
            visible={reasonModal != null}
            title={reasonModal === 'void' ? 'Anular liquidación' : 'Descartar liquidación'}
            subtitle={
              reasonModal === 'void'
                ? 'Las comisiones vuelven a pendientes.'
                : 'Las comisiones vuelven a pendientes para liquidar de nuevo.'
            }
            confirmLabel={reasonModal === 'void' ? 'Anular' : 'Descartar'}
            defaultReason={reasonModal === 'void' ? 'Anulada por dueño' : 'Descartada por dueño'}
            busy={busy}
            onClose={() => setReasonModal(null)}
            onConfirm={(reason) => void handleReasonConfirm(reason)}
          />
          {ConfirmModal}
        </>
      }
    />
  );
}

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.xl,
  },
  statusBadge: {
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
  },
  statusBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    fontFamily: ticketFonts.sans,
  },
  metaList: {
    marginTop: spacing.sm,
    gap: spacing.xs,
  },
  metaLine: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  sectionKicker: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.88,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
    marginBottom: spacing.sm,
  },
  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  payInputs: {
    marginBottom: spacing.sm,
  },
  saleLink: { marginTop: 4, alignSelf: 'flex-start' },
  saleLinkText: { fontSize: 12, fontWeight: '600', color: ticketColors.brandInk, fontFamily: ticketFonts.sans },
});
